// Admin dashboard (plan §admin). Visible only to admin/superuser — a plain
// player is redirected home. Three tabs:
//   • Members — roles, ban/unban, delete (rules enforced server-side; the UI
//               only hides/disables what the actor can't do).
//   • Groups  — create static/live groups, manage static rosters, delete.
//   • Danger  — superuser only: the delegation toggle + a dev "Reset DB".

import {
  api,
  ApiError,
  type AdminGroup,
  type AdminUserRow,
  type GatewayAdminStatus,
  type User,
} from "../api.ts";
import { esc, h, setMsg } from "../dom.ts";
import { t, tp } from "../i18n/index.ts";
import { alertDialog, confirmDialog } from "../ui/confirm.ts";
import { pagedList } from "../ui/pagedList.ts";
import { clearBotGames } from "../game/botHistory.ts";
import { navigate } from "../router.ts";
import { currentUser } from "../session.ts";
import { topNav } from "../ui/topnav.ts";
import { renderReports } from "../play/moderation.ts";

export async function adminScreen(): Promise<HTMLElement> {
  const user = currentUser();
  if (!user) {
    navigate("/login");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }
  if (user.role !== "admin" && user.role !== "superuser") {
    navigate("/");
    return h(`<main class="card"><p class="msg">${t("common.redirecting")}</p></main>`);
  }

  // The delegation setting gates whether a plain admin may change roles; the
  // membership settings drive the public "Become a member" form. Kept in a mutable
  // box so the Danger toggles and the Members tab stay in sync.
  const settings = { admin_can_delegate: false, membership_open: false, membership_notify: "" };
  try {
    const s = await api.adminSettings();
    settings.admin_can_delegate = s.admin_can_delegate;
    settings.membership_open = s.membership_open;
    settings.membership_notify = s.membership_notify;
  } catch {
    /* non-fatal: defaults to no delegation / closed membership */
  }

  const isSuper = user.role === "superuser";
  const el = h(`
    <main class="card wide admin-card">
      <section class="panel">
        <div class="tabs" role="tablist">
          <button class="tab-btn active" role="tab" data-tab="members">${t("admin.tabMembers")}</button>
          <button class="tab-btn" role="tab" data-tab="groups">${t("admin.tabGroups")}</button>
          <button class="tab-btn" role="tab" data-tab="messaging">${t("admin.tabMessaging")}</button>
          <button class="tab-btn" role="tab" data-tab="reports">Player reports</button>
          ${isSuper ? `<button class="tab-btn" role="tab" data-tab="danger">${t("admin.tabDanger")}</button>` : ""}
        </div>
        <div class="tab-body"></div>
      </section>
    </main>
  `);
  el.prepend(topNav("admin"));

  const body = el.querySelector<HTMLElement>(".tab-body")!;
  const tabs = el.querySelector<HTMLElement>(".tabs")!;

  const show = (tab: string) => {
    tabs.querySelectorAll<HTMLElement>(".tab-btn").forEach((b) =>
      b.classList.toggle("active", b.dataset.tab === tab),
    );
    body.replaceChildren();
    if (tab === "groups") void renderGroups(body);
    else if (tab === "messaging") void renderMessaging(body);
    else if (tab === "reports") void renderReports(body);
    else if (tab === "danger" && isSuper) void renderDanger(body, settings);
    else void renderMembers(body, user, settings);
  };

  tabs.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>(".tab-btn");
    if (btn?.dataset.tab) show(btn.dataset.tab);
  });

  show("members");
  return el;
}

// ---------------------------------------------------------------------------
// Members tab
// ---------------------------------------------------------------------------

async function renderMembers(
  body: HTMLElement,
  me: User,
  settings: { admin_can_delegate: boolean },
): Promise<void> {
  const wrap = h(`
    <div class="admin-members">
      <p class="tagline">${t("admin.membersTagline")}</p>
      <div class="admin-filters">
        <input class="admin-search" type="search" enterkeyhint="search" autocomplete="off"
               aria-label="${t("admin.searchLabel")}" placeholder="${t("admin.searchPlaceholder")}" />
        <select class="filter-role" aria-label="${t("admin.thRole")}">
          <option value="">${t("admin.allRoles")}</option>
          <option value="admin">${t("admin.roleAdmins")}</option>
          <option value="player">${t("admin.rolePlayers")}</option>
          <option value="superuser">${t("admin.roleSuperuser")}</option>
        </select>
        <select class="filter-banned" aria-label="${t("admin.thStatus")}">
          <option value="">${t("admin.anyStatus")}</option>
          <option value="active">${t("admin.statusActive")}</option>
          <option value="banned">${t("admin.statusBanned")}</option>
        </select>
      </div>
      <div class="admin-table-slot"></div>
    </div>`);
  body.append(wrap);
  const searchInput = wrap.querySelector<HTMLInputElement>(".admin-search")!;
  const roleSel = wrap.querySelector<HTMLSelectElement>(".filter-role")!;
  const bannedSel = wrap.querySelector<HTMLSelectElement>(".filter-banned")!;
  const slot = wrap.querySelector<HTMLElement>(".admin-table-slot")!;

  const canDelegate = me.role === "superuser" || (me.role === "admin" && settings.admin_can_delegate);
  const canActOn = (u: AdminUserRow): boolean => {
    if (u.id === me.id) return false; // never yourself
    if (u.role === "superuser") return false; // never the superuser
    if (me.role !== "superuser" && u.role === "admin") return false; // admins ≠ each other
    return true;
  };

  // Current filter state, read by fetchPage on every (re)load.
  let query = "";
  let role = "";
  let banned: boolean | undefined;

  const memberRow = (u: AdminUserRow): HTMLElement => {
    const tr = document.createElement("tr");
    const roleLabel =
      u.role === "admin" ? t("admin.roleAdmin") : u.role === "superuser" ? t("admin.roleSuper") : t("admin.rolePlayer");
    // Each cell carries its column name so the ≤720px stacked-card layout can
    // label it (the header row is hidden there).
    tr.innerHTML = `
      <td class="cell-member">
        <a data-link href="/player/${encodeURIComponent(u.username)}">${esc(u.username)}</a>
        <span class="fine">${esc(u.email)}</span>
      </td>
      <td class="cell-role" data-label="${t("admin.thRole")}"><span class="role-badge role-${u.role}">${esc(roleLabel)}</span></td>
      <td class="cell-status" data-label="${t("admin.thStatus")}">${u.banned ? `<span class="banned-badge">${t("admin.badgeBanned")}</span>` : `<span class="status-badge">${t("admin.badgeActive")}</span>`}</td>
      <td class="cell-inviter" data-label="${t("admin.thInvitedBy")}">${u.inviter ? esc(u.inviter) : "—"}</td>
      <td class="cell-actions"><div class="row-actions"></div></td>`;
    const actions = tr.querySelector<HTMLElement>(".row-actions")!;

    if (!canActOn(u)) {
      actions.innerHTML = `<span class="fine">—</span>`;
      return tr;
    }

    const act = async (fn: () => Promise<unknown>, confirmMsg?: string) => {
      if (confirmMsg && !(await confirmDialog(confirmMsg, { danger: true }))) return;
      actions.querySelectorAll("button").forEach((b) => (b.disabled = true));
      try {
        await fn();
        await paged.reload(); // reflect the change
      } catch (err) {
        actions.querySelectorAll("button").forEach((b) => (b.disabled = false));
        const note = document.createElement("span");
        note.className = "msg bad inline";
        note.textContent = err instanceof ApiError ? err.message : t("admin.actionFailed");
        actions.append(note);
        window.setTimeout(() => note.remove(), 4000);
      }
    };

    // Role delegation (superuser always; admin only when allowed).
    if (canDelegate) {
      if (u.role === "player") {
        actions.append(
          button(t("admin.makeAdmin"), "ghost", () => act(() => api.adminSetRole(u.id, "admin"))),
        );
      } else if (u.role === "admin") {
        actions.append(
          button(t("admin.makePlayer"), "ghost", () => act(() => api.adminSetRole(u.id, "player"))),
        );
      }
    }
    // Ban / unban.
    actions.append(
      u.banned
        ? button(t("admin.unban"), "ghost", () => act(() => api.adminUnban(u.id)))
        : button(t("admin.ban"), "ghost", () => act(() => api.adminBan(u.id))),
    );
    // Delete (permanent).
    actions.append(
      button(t("admin.delete"), "danger", () =>
        act(() => api.adminDeleteUser(u.id), t("admin.deleteUserQ", { name: u.username })),
      ),
    );
    return tr;
  };

  const table = h(`
    <table class="members-grid">
      <thead><tr>
        <th>${t("admin.thMember")}</th><th>${t("admin.thRole")}</th><th>${t("admin.thStatus")}</th><th>${t("admin.thInvitedBy")}</th><th class="th-actions">${t("admin.thActions")}</th>
      </tr></thead>
      <tbody></tbody>
    </table>`);
  const tbody = table.querySelector("tbody")!;

  const paged = pagedList<AdminUserRow>({
    rowsContainer: tbody,
    rowsHost: table,
    fetchPage: (offset, limit) =>
      api
        .adminUsers({ limit, offset, q: query || undefined, role: role || undefined, banned })
        .then((r) => ({ items: r.users, has_more: r.has_more })),
    renderRow: memberRow,
    emptyText: () =>
      query || role || banned != null ? t("admin.noMatchFilters") : t("admin.noMembers"),
    errorText: t("admin.membersFailed"),
  });
  slot.append(paged.element);

  // Debounced search + instant filter chips, each restarting from page 1.
  let debounce: number | undefined;
  searchInput.addEventListener("input", () => {
    if (debounce) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      const next = searchInput.value.trim();
      if (next === query) return;
      query = next;
      void paged.reload();
    }, 200);
  });
  roleSel.addEventListener("change", () => {
    role = roleSel.value;
    void paged.reload();
  });
  bannedSel.addEventListener("change", () => {
    banned = bannedSel.value === "" ? undefined : bannedSel.value === "banned";
    void paged.reload();
  });
}

// ---------------------------------------------------------------------------
// Groups tab
// ---------------------------------------------------------------------------

async function renderGroups(body: HTMLElement): Promise<void> {
  const wrap = h(`
    <div class="admin-groups">
      <p class="tagline">${t("admin.groupsTagline")}</p>
      <div class="group-create"></div>
      <div class="group-list"><p class="fine">${t("common.loading")}</p></div>
    </div>`);
  body.append(wrap);
  wrap.querySelector<HTMLElement>(".group-create")!.append(createGroupForm(() => load()));
  const listEl = wrap.querySelector<HTMLElement>(".group-list")!;

  const load = async () => {
    let groups: AdminGroup[];
    try {
      ({ groups } = await api.adminGroups());
    } catch (err) {
      listEl.textContent = err instanceof ApiError ? err.message : t("admin.groupsFailed");
      return;
    }
    listEl.replaceChildren();
    for (const g of groups) listEl.append(groupCard(g, () => load()));
  };
  await load();
}

function createGroupForm(onCreated: () => void): HTMLElement {
  const form = h(`
    <details class="group-new">
      <summary>${t("admin.newGroup")}</summary>
      <div class="group-new-body">
        <label class="field"><span>${t("admin.name")}</span><input name="name" placeholder="${t("admin.namePlaceholder")}" /></label>
        <label class="field"><span>${t("admin.kind")}</span>
          <select name="kind"><option value="static">${t("admin.kindStatic")}</option><option value="live">${t("admin.kindLive")}</option></select>
        </label>
        <div class="live-spec" hidden>
          <label class="field"><span>${t("admin.rule")}</span>
            <select name="spectype">
              <option value="invite_distance">${t("admin.ruleDistance")}</option>
              <option value="rating_range">${t("admin.ruleRating")}</option>
            </select>
          </label>
          <label class="field dist-field"><span>${t("admin.maxHops")}</span><input name="max" type="number" min="1" max="10" value="3" /></label>
          <div class="range-fields" hidden>
            <label class="field"><span>${t("admin.speed")}</span><select name="speed"><option value="blitz">${t("chess.blitz")}</option><option value="rapid">${t("chess.rapid")}</option></select></label>
            <label class="field"><span>${t("admin.min")}</span><input name="min" type="number" value="1400" /></label>
            <label class="field"><span>${t("admin.max")}</span><input name="rmax" type="number" value="1600" /></label>
          </div>
        </div>
        <button class="btn primary sm" type="button" data-create>${t("admin.createGroup")}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </div>
    </details>`);

  const kind = form.querySelector<HTMLSelectElement>('select[name="kind"]')!;
  const liveSpec = form.querySelector<HTMLElement>(".live-spec")!;
  const specType = form.querySelector<HTMLSelectElement>('select[name="spectype"]')!;
  const distField = form.querySelector<HTMLElement>(".dist-field")!;
  const rangeFields = form.querySelector<HTMLElement>(".range-fields")!;
  const msg = form.querySelector<HTMLElement>(".msg")!;

  const sync = () => {
    liveSpec.hidden = kind.value !== "live";
    const range = specType.value === "rating_range";
    distField.hidden = range;
    rangeFields.hidden = !range;
  };
  kind.addEventListener("change", sync);
  specType.addEventListener("change", sync);
  sync();

  form.querySelector<HTMLButtonElement>("[data-create]")!.addEventListener("click", async () => {
    const name = form.querySelector<HTMLInputElement>('input[name="name"]')!.value.trim();
    if (!name) {
      setMsg(msg, t("admin.nameNeeded"), "bad");
      return;
    }
    let spec: string | undefined;
    if (kind.value === "live") {
      if (specType.value === "invite_distance") {
        const max = Number(form.querySelector<HTMLInputElement>('input[name="max"]')!.value);
        spec = JSON.stringify({ type: "invite_distance", max });
      } else {
        const speed = form.querySelector<HTMLSelectElement>('select[name="speed"]')!.value;
        const min = Number(form.querySelector<HTMLInputElement>('input[name="min"]')!.value);
        const rmax = Number(form.querySelector<HTMLInputElement>('input[name="rmax"]')!.value);
        spec = JSON.stringify({ type: "rating_range", speed, min, max: rmax });
      }
    }
    setMsg(msg, t("admin.creating"));
    try {
      await api.adminCreateGroup(name, kind.value as "static" | "live", spec);
      setMsg(msg, t("admin.created"), "ok");
      (form as HTMLDetailsElement).open = false;
      form.querySelector<HTMLInputElement>('input[name="name"]')!.value = "";
      onCreated();
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("admin.createFailed"), "bad");
    }
  });

  return form;
}

function groupCard(g: AdminGroup, onChanged: () => void): HTMLElement {
  const isDefault = g.id === "grp_default_neighborhood";
  const card = h(`
    <div class="group-card">
      <div class="group-head">
        <div>
          <strong>${esc(g.name)}</strong>
          <span class="group-kind kind-${g.kind}">${g.kind === "live" ? t("admin.kindBadgeLive") : t("admin.kindBadgeStatic")}</span>
          <span class="fine">${g.description ? esc(g.description) : t("admin.handPicked")} · ${tp("plural.members", g.member_count)}</span>
        </div>
        <div class="group-head-actions"></div>
      </div>
      <div class="group-members" hidden></div>
    </div>`);
  const headActions = card.querySelector<HTMLElement>(".group-head-actions")!;
  const membersBox = card.querySelector<HTMLElement>(".group-members")!;

  headActions.append(
    button(t("admin.members"), "ghost", () => {
      if (membersBox.hidden) {
        membersBox.hidden = false;
        void renderGroupMembers(membersBox, g);
      } else {
        membersBox.hidden = true;
        membersBox.replaceChildren();
      }
    }),
  );
  if (!isDefault) {
    headActions.append(
      button(t("admin.delete"), "danger", async () => {
        if (!(await confirmDialog(t("admin.deleteGroupQ", { name: g.name }), { danger: true }))) return;
        try {
          await api.adminDeleteGroup(g.id);
          onChanged();
        } catch (err) {
          void alertDialog(err instanceof ApiError ? err.message : t("admin.deleteFailed"));
        }
      }),
    );
  }
  return card;
}

async function renderGroupMembers(box: HTMLElement, g: AdminGroup): Promise<void> {
  box.innerHTML = `<p class="fine">${t("admin.loadingMembers")}</p>`;
  let data: Awaited<ReturnType<typeof api.adminGroupMembers>>;
  try {
    data = await api.adminGroupMembers(g.id);
  } catch (err) {
    box.textContent = err instanceof ApiError ? err.message : t("admin.membersFailed");
    return;
  }

  box.replaceChildren();
  // A viewer-relative live group (invite distance) now returns the roster
  // computed for the requesting admin (plan A1) — note it, then render it.
  if (data.relative) {
    box.append(h(`<p class="fine">${esc(t("admin.relativeNote"))}</p>`));
  }

  const list = h(`<div class="member-chips"></div>`);
  if (data.members.length === 0) list.innerHTML = `<span class="fine">${t("admin.noMembers")}</span>`;
  for (const m of data.members) {
    const chip = document.createElement("span");
    chip.className = "member-chip";
    chip.innerHTML = `${esc(m.username)}`;
    if (g.kind === "static") {
      const x = document.createElement("button");
      x.type = "button";
      x.className = "chip-x";
      x.textContent = "×";
      x.title = t("admin.remove", { name: m.username });
      x.setAttribute("aria-label", t("admin.remove", { name: m.username }));
      x.addEventListener("click", async () => {
        try {
          await api.adminRemoveGroupMember(g.id, m.id);
          void renderGroupMembers(box, g);
        } catch {
          /* ignore; reload shows truth */
        }
      });
      chip.append(x);
    }
    list.append(chip);
  }
  box.append(list);

  // Static groups get a search-as-you-type adder (plan item 5): never materialize
  // the whole roster — query the server for a handful of matches per keystroke.
  if (g.kind === "static") {
    const adder = h(`
      <div class="member-add">
        <input class="add-search" type="search" enterkeyhint="search" autocomplete="off"
               aria-label="${t("admin.addMemberLabel")}" placeholder="${t("admin.addMember")}" />
        <div class="add-results" role="listbox" hidden></div>
      </div>`);
    const input = adder.querySelector<HTMLInputElement>(".add-search")!;
    const results = adder.querySelector<HTMLElement>(".add-results")!;
    const present = new Set(data.members.map((m) => m.id));

    const add = async (uid: string) => {
      try {
        await api.adminAddGroupMember(g.id, uid);
        void renderGroupMembers(box, g);
      } catch (err) {
        void alertDialog(err instanceof ApiError ? err.message : t("admin.addFailed"));
      }
    };
    const clearResults = () => {
      results.replaceChildren();
      results.hidden = true;
    };

    let debounce: number | undefined;
    let seq = 0; // guards against an older, slower response overwriting a newer one
    input.addEventListener("input", () => {
      if (debounce) window.clearTimeout(debounce);
      const q = input.value.trim();
      if (!q) {
        clearResults();
        return;
      }
      debounce = window.setTimeout(async () => {
        const mine = ++seq;
        let users: AdminUserRow[];
        try {
          ({ users } = await api.adminUsers({ q, limit: 10 }));
        } catch {
          return;
        }
        if (mine !== seq || !input.isConnected) return; // a newer keystroke won
        const matches = users.filter((u) => !present.has(u.id));
        results.replaceChildren();
        if (matches.length === 0) {
          results.append(h(`<div class="add-none fine">${t("admin.noMatches")}</div>`));
        } else {
          for (const u of matches) {
            const opt = document.createElement("button");
            opt.type = "button";
            opt.className = "add-option";
            opt.setAttribute("role", "option");
            opt.innerHTML = `<strong>${esc(u.username)}</strong> <span class="fine">${esc(u.email)}</span>`;
            opt.addEventListener("click", () => add(u.id));
            results.append(opt);
          }
        }
        results.hidden = false;
      }, 200);
    });
    // Let a click on a result land before the blur tears the dropdown down.
    input.addEventListener("blur", () => window.setTimeout(clearResults, 150));
    box.append(adder);
  }
}

// ---------------------------------------------------------------------------
// Messaging operations
// ---------------------------------------------------------------------------

async function renderMessaging(body: HTMLElement): Promise<void> {
  const wrap = h(`
    <div class="admin-messaging">
      <div class="gateway-admin-head">
        <p class="tagline">${t("admin.messagingTagline")}</p>
        <button class="btn ghost sm" type="button" data-refresh>${t("admin.messagingRefresh")}</button>
      </div>
      <p class="msg" role="status" aria-live="polite">${t("common.loading")}</p>
      <div class="gateway-admin-content"></div>
    </div>
  `);
  body.append(wrap);
  const content = wrap.querySelector<HTMLElement>(".gateway-admin-content")!;
  const msg = wrap.querySelector<HTMLElement>(".msg")!;
  const refresh = wrap.querySelector<HTMLButtonElement>("[data-refresh]")!;

  const load = async () => {
    refresh.disabled = true;
    setMsg(msg, t("common.loading"));
    try {
      const status = await api.gatewayAdminStatus();
      renderGatewayStatus(content, msg, status, load);
      setMsg(msg, "");
    } catch (error) {
      content.replaceChildren();
      setMsg(
        msg,
        error instanceof ApiError ? error.message : t("admin.messagingLoadFailed"),
        "bad",
      );
    } finally {
      refresh.disabled = false;
    }
  };
  refresh.addEventListener("click", () => void load());
  await load();
}

function renderGatewayStatus(
  content: HTMLElement,
  msg: HTMLElement,
  status: GatewayAdminStatus,
  reload: () => Promise<void>,
): void {
  const oldest =
    status.queue.oldest_age_secs == null
      ? "—"
      : t("admin.messagingAge", { seconds: status.queue.oldest_age_secs });
  const providerRows = status.providers
    .map(
      (provider) => `
        <tr>
          <td><strong>${esc(provider.provider)}</strong><span class="fine">${esc(provider.account_id)}</span></td>
          <td><span class="gateway-health health-${esc(provider.state)}">${esc(provider.state)}</span></td>
          <td>${gatewayTime(provider.last_success_at)}</td>
          <td class="fine">${provider.last_error ? esc(provider.last_error) : "—"}</td>
          <td>${
            provider.configured
              ? `<button class="btn ${provider.enabled ? "danger" : "ghost"} sm"
                    type="button" data-provider="${esc(provider.provider)}"
                    data-account="${esc(provider.account_id)}"
                    data-enabled="${provider.enabled}">
              ${provider.enabled ? t("admin.messagingDisable") : t("admin.messagingEnable")}
            </button>`
              : "—"
          }
          </td>
        </tr>`,
    )
    .join("");
  const queuedRows = status.queued
    .slice(0, 20)
    .map(
      (item) => `
        <tr><td><code>${esc(item.id)}</code></td><td>${esc(item.kind)}</td>
        <td>${esc(item.state)}</td><td>${item.attempt_count}</td>
        <td class="fine">${item.last_error ? esc(item.last_error) : "—"}</td></tr>`,
    )
    .join("");
  const deadRows = status.dead_letters
    .map(
      (item) => `
        <tr><td><code>${esc(item.id)}</code></td><td>${esc(item.kind)}</td>
        <td>${esc(item.state)}</td><td class="fine">${item.last_error ? esc(item.last_error) : "—"}</td>
        <td>${
          item.retryable
            ? `<button class="btn ghost sm" type="button" data-retry="${esc(item.id)}">${t("admin.messagingRetry")}</button>`
            : "—"
        }</td></tr>`,
    )
    .join("");
  content.innerHTML = `
    <div class="gateway-admin-summary">
      <div><strong>${status.queue.depth}</strong><span>${t("admin.messagingQueueDepth")}</span></div>
      <div><strong>${esc(oldest)}</strong><span>${t("admin.messagingOldest")}</span></div>
      <div><strong>${status.queue.dead_letter_count}</strong><span>${t("admin.messagingDead")}</span></div>
    </div>
    ${
      status.alerts.length
        ? `<p class="gateway-admin-alert">${t("admin.messagingAlerts", { count: status.alerts.length })}</p>`
        : ""
    }
    <h3>${t("admin.messagingProviders")}</h3>
    <div class="table-scroll"><table class="admin-table gateway-admin-table">
      <thead><tr><th>${t("admin.messagingProvider")}</th><th>${t("admin.messagingState")}</th>
      <th>${t("admin.messagingLastSuccess")}</th><th>${t("admin.messagingLastError")}</th>
      <th>${t("admin.thActions")}</th></tr></thead>
      <tbody>${providerRows || `<tr><td colspan="5">${t("admin.messagingNoProviders")}</td></tr>`}</tbody>
    </table></div>
    <h3>${t("admin.messagingQueued")}</h3>
    <div class="table-scroll"><table class="admin-table gateway-admin-table">
      <thead><tr><th>ID</th><th>${t("admin.messagingKind")}</th><th>${t("admin.messagingState")}</th>
      <th>${t("admin.messagingAttempts")}</th><th>${t("admin.messagingLastError")}</th></tr></thead>
      <tbody>${queuedRows || `<tr><td colspan="5">${t("admin.messagingNoneQueued")}</td></tr>`}</tbody>
    </table></div>
    <h3>${t("admin.messagingDeadLetters")}</h3>
    <div class="table-scroll"><table class="admin-table gateway-admin-table">
      <thead><tr><th>ID</th><th>${t("admin.messagingKind")}</th><th>${t("admin.messagingState")}</th>
      <th>${t("admin.messagingLastError")}</th><th>${t("admin.thActions")}</th></tr></thead>
      <tbody>${deadRows || `<tr><td colspan="5">${t("admin.messagingNoneDead")}</td></tr>`}</tbody>
    </table></div>
  `;

  content.querySelectorAll<HTMLButtonElement>("[data-provider]").forEach((control) => {
    control.addEventListener("click", async () => {
      const provider = control.dataset.provider!;
      const account = control.dataset.account!;
      const wasEnabled = control.dataset.enabled === "true";
      if (
        wasEnabled &&
        !(await confirmDialog(
          t("admin.messagingDisableQ", { provider, account }),
          { danger: true },
        ))
      ) {
        return;
      }
      control.disabled = true;
      try {
        await api.gatewayAdminSetProvider(provider, account, !wasEnabled);
        await reload();
      } catch (error) {
        control.disabled = false;
        setMsg(
          msg,
          error instanceof ApiError ? error.message : t("admin.messagingControlFailed"),
          "bad",
        );
      }
    });
  });
  content.querySelectorAll<HTMLButtonElement>("[data-retry]").forEach((control) => {
    control.addEventListener("click", async () => {
      const id = control.dataset.retry!;
      if (!(await confirmDialog(t("admin.messagingRetryQ", { id })))) return;
      control.disabled = true;
      try {
        await api.gatewayAdminRetry(id);
        await reload();
      } catch (error) {
        control.disabled = false;
        setMsg(
          msg,
          error instanceof ApiError ? error.message : t("admin.messagingRetryFailed"),
          "bad",
        );
      }
    });
  });
}

function gatewayTime(value: number | null): string {
  if (value == null) return "—";
  return esc(new Date(value * 1000).toLocaleString());
}

// ---------------------------------------------------------------------------
// Danger zone (superuser only)
// ---------------------------------------------------------------------------

async function renderDanger(
  body: HTMLElement,
  settings: { admin_can_delegate: boolean; membership_open: boolean; membership_notify: string },
): Promise<void> {
  const wrap = h(`
    <div class="admin-danger">
      <section class="danger-block">
        <h3>${t("admin.delegation")}</h3>
        <label class="toggle-row">
          <input type="checkbox" id="delegate" ${settings.admin_can_delegate ? "checked" : ""} />
          <span>${t("admin.delegationHelp")}</span>
        </label>
        <p class="msg" id="delegate-msg" role="status" aria-live="polite"></p>
      </section>

      <section class="danger-block">
        <h3>${t("admin.membership")}</h3>
        <label class="toggle-row">
          <input type="checkbox" id="mopen" ${settings.membership_open ? "checked" : ""} />
          <span>${t("admin.membershipOpen")}</span>
        </label>
        <label class="field" style="margin-top:.6rem">
          <span>${t("admin.membershipNotify")}</span>
          <input id="mnotify" type="email" value="${esc(settings.membership_notify)}" placeholder="igor.tavcar@gmail.com" autocomplete="off" />
        </label>
        <div class="reset-row"><button class="btn" id="mnotify-save" type="button">${t("common.save")}</button></div>
        <p class="msg" id="membership-msg" role="status" aria-live="polite"></p>
      </section>

      <section class="danger-block danger">
        <h3>${t("admin.resetDb")}</h3>
        <p class="fine">${t("admin.resetHelp")}</p>
        <div class="reset-row">
          <input id="reset-confirm" placeholder="${t("admin.resetPlaceholder")}" autocomplete="off" />
          <button class="btn danger" id="reset-btn" type="button" disabled>${t("admin.resetDb")}</button>
        </div>
        <p class="msg" id="reset-msg" role="status" aria-live="polite"></p>
      </section>
    </div>`);
  body.append(wrap);

  const delegate = wrap.querySelector<HTMLInputElement>("#delegate")!;
  const delegateMsg = wrap.querySelector<HTMLElement>("#delegate-msg")!;
  delegate.addEventListener("change", async () => {
    try {
      const res = await api.adminSetSettings({ admin_can_delegate: delegate.checked });
      settings.admin_can_delegate = res.admin_can_delegate;
      setMsg(delegateMsg, res.admin_can_delegate ? t("admin.delegationOn") : t("admin.delegationOff"), "ok");
    } catch (err) {
      delegate.checked = settings.admin_can_delegate; // revert
      setMsg(delegateMsg, err instanceof ApiError ? err.message : t("settings.saveFailed"), "bad");
    }
  });

  // Membership: the checkbox opens/closes the public form; the email field is the
  // destination applications are mailed to. Each PATCHes independently.
  const mopen = wrap.querySelector<HTMLInputElement>("#mopen")!;
  const mnotify = wrap.querySelector<HTMLInputElement>("#mnotify")!;
  const membershipMsg = wrap.querySelector<HTMLElement>("#membership-msg")!;
  mopen.addEventListener("change", async () => {
    try {
      const res = await api.adminSetSettings({ membership_open: mopen.checked });
      settings.membership_open = res.membership_open;
      setMsg(membershipMsg, res.membership_open ? t("admin.applicationsOpen") : t("admin.applicationsClosed"), "ok");
    } catch (err) {
      mopen.checked = settings.membership_open; // revert
      setMsg(membershipMsg, err instanceof ApiError ? err.message : t("settings.saveFailed"), "bad");
    }
  });
  wrap.querySelector<HTMLButtonElement>("#mnotify-save")!.addEventListener("click", async () => {
    const v = mnotify.value.trim();
    if (!v) { setMsg(membershipMsg, t("admin.membershipNotify"), "bad"); return; }
    try {
      const res = await api.adminSetSettings({ membership_notify: v });
      settings.membership_notify = res.membership_notify;
      mnotify.value = res.membership_notify;
      setMsg(membershipMsg, t("settings.saved"), "ok");
    } catch (err) {
      setMsg(membershipMsg, err instanceof ApiError ? err.message : t("settings.saveFailed"), "bad");
    }
  });

  const confirmInput = wrap.querySelector<HTMLInputElement>("#reset-confirm")!;
  const resetBtn = wrap.querySelector<HTMLButtonElement>("#reset-btn")!;
  const resetMsg = wrap.querySelector<HTMLElement>("#reset-msg")!;
  confirmInput.addEventListener("input", () => {
    resetBtn.disabled = confirmInput.value !== "RESET";
  });
  resetBtn.addEventListener("click", async () => {
    if (!(await confirmDialog(t("admin.wipeQ"), { danger: true }))) return;
    resetBtn.disabled = true;
    setMsg(resetMsg, t("admin.resetting"));
    try {
      await api.adminReset();
      // The server DB is wiped; also drop this browser's client-side computer-game
      // history so "Recent computer games" doesn't linger after a full reset.
      clearBotGames();
      setMsg(resetMsg, t("admin.resetDone"), "ok");
      window.setTimeout(() => window.location.assign("/"), 800);
    } catch (err) {
      resetBtn.disabled = false;
      setMsg(resetMsg, err instanceof ApiError ? err.message : t("admin.resetFailed"), "bad");
    }
  });
}

// ---------------------------------------------------------------------------
// Small helper
// ---------------------------------------------------------------------------

function button(label: string, variant: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = `btn ${variant} sm`;
  b.textContent = label;
  b.addEventListener("click", onClick);
  return b;
}
